import React from 'react';
import { salonInfo } from '../data/mock';
import { useLanguage } from '../i18n/LanguageContext';

export const Hero = () => {
  const { t } = useLanguage();

  const openWhatsApp = () => {
    window.open(`https://wa.me/${salonInfo.whatsapp.replace(/\s|\+/g, '')}`, '_blank');
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative bg-neutral-950 text-neutral-100 pt-8 md:pt-12 pb-24 md:pb-28 overflow-hidden"
    >
      <div className="absolute top-0 -left-20 w-[520px] h-[520px] rounded-full bg-[#c8b6d9] opacity-[0.06] blur-[130px] pointer-events-none"></div>
      <div className="absolute top-1/2 -right-20 w-[440px] h-[440px] rounded-full bg-[#9bab9a] opacity-[0.05] blur-[130px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full relative">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="flex items-center gap-4 mb-8">
              <span className="divider-line"></span>
              <span className="eyebrow text-lilac">{t.hero.eyebrow}</span>
            </div>

            <h1 className="font-display text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] leading-[1.05] text-neutral-100 tracking-tight mb-8">
              {t.hero.titleA}
              <span className="text-lilac italic font-light">{t.hero.titleB}</span>
              {t.hero.titleC}
              <span className="italic font-light text-sage">{t.hero.titleD}</span>
            </h1>

            <p className="font-display italic text-xl md:text-2xl lg:text-3xl text-neutral-300 font-light mb-10 max-w-xl leading-snug">
              {t.hero.tagline1}
              <span className="text-lilac">{t.hero.tagline2}</span>
              {t.hero.tagline3}
              <span className="text-sage">{t.hero.tagline4}</span>
            </p>

            <div className="border-l-2 border-lilac/40 pl-6 py-1 mb-12 max-w-xl space-y-4">
              <p className="text-lg md:text-xl text-neutral-200 leading-relaxed font-light">
                {t.hero.bodyA}
              </p>
              <p className="font-display italic text-lg md:text-xl text-lilac leading-relaxed">
                {t.hero.bodyMission}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 mb-14">
              <button
                onClick={openWhatsApp}
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center justify-center gap-4 bg-lilac hover:bg-white text-neutral-950 px-8 py-5 eyebrow transition-all duration-500"
              >
                {t.cta.whatsapp}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>
              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-4 eyebrow text-neutral-300 hover:text-lilac transition-colors"
              >
                {t.cta.discoverServices}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-10 gap-y-6 pt-8 border-t border-white/10">
              <a
                href="https://share.google/XsDt19sJzp8dbpBmz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                aria-label="Google 5-Sterne-Bewertungen ansehen"
                className="group inline-block"
              >
                <p
                  data-testid="google-reviews-stars"
                  className="text-xl md:text-2xl text-lilac leading-none tracking-[0.15em]"
                  aria-hidden="true"
                >
                  ★★★★★
                </p>
                <p className="eyebrow text-neutral-500 mt-2 group-hover:text-lilac transition-colors">
                  {t.hero.stats.rating}
                </p>
              </a>
              <div className="h-10 w-px bg-white/10 hidden sm:block"></div>
              <div>
                <p className="font-display text-2xl md:text-3xl text-lilac">KR</p>
                <p className="eyebrow text-neutral-500 mt-1">{t.hero.stats.certified}</p>
              </div>
              <div className="h-10 w-px bg-white/10 hidden sm:block"></div>
              <div>
                <p className="font-display text-2xl md:text-3xl text-sage">10+</p>
                <p className="eyebrow text-neutral-500 mt-1">{t.hero.stats.rituals}</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 order-1 md:order-2">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_korean-glow-9/artifacts/8ga8dkre_1000160246.jpg"
                  alt="Agnes — Korea Mikwang Beauty Academy"
                  data-testid="hero-image"
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(0.95) brightness(0.98)' }}
                />
              </div>
              <div className="absolute -bottom-3 -right-3 w-20 h-20 md:w-28 md:h-28 border border-lilac/30 pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
